import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import toQueryParams from 'jquery-param'
import type { $Values } from 'utility-types'

import { observer } from 'mobx-react'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

import { isManager } from 'modules/employees/lib/utils'
import type { AnalyticsFilterForm } from 'modules/surveys/hooks/useAnalyticsMultiFilters'
import type SurveyForm from 'modules/surveys/store/models/Form'
import type Survey from 'modules/surveys/store/models/v2/Survey'
import memberships from 'modules/teams/store/collections/memberships'
import teams from 'modules/teams/store/collections/teams'
import { userIsTeamLead } from 'modules/teams/store/userIsTeamLead'

interface ButtonProps {
  form: SurveyForm | Survey
  surveyAnalyticsFilter: AnalyticsFilterForm | undefined
  canEditSurvey: boolean
  hasAnswers: boolean
}

const EXPORT_FORMATS = {
  csv: 'csv',
  xlsx: 'xlsx',
}

class DataParams {
  [key: string]: number | number[] | string
}

const RightActions = ({ form, surveyAnalyticsFilter, canEditSurvey, hasAnswers }: ButtonProps) => {
  const { employee } = useCurrentSession()

  if (!hasAnswers) return null

  const canSeeInfo =
    form?.attributes.visible_to_managers &&
    (isManager(employee) ||
      (users.current.employee
        ? userIsTeamLead(teams, memberships, users.current.employee.id)
        : false))

  const onDownload = async (exportFormat: $Values<typeof EXPORT_FORMATS>) => {
    const params: DataParams = {
      form_id: form.id,
      export_format: exportFormat,
    }

    if (surveyAnalyticsFilter?.isActive && surveyAnalyticsFilter?.default.length) {
      const simpleFilterValues = surveyAnalyticsFilter.default[0]?.values

      if (simpleFilterValues.field === 'team') {
        params.teams = [simpleFilterValues.value]
      } else if (simpleFilterValues.field === 'location') {
        params.locations = [simpleFilterValues.value]
      }
    }

    const url = buildUrl(`/surveys/exports/download?${toQueryParams(params)}`)
    window.location.href = url
  }

  if (!canEditSurvey && !canSeeInfo) return null

  return (
    <PopoverMenu type="list">
      <PopoverMenu.Trigger>
        <Button.Decorative type="secondary" iconName="ellipsis" />
      </PopoverMenu.Trigger>
      <PopoverMenu.Content side="bottom" align="start">
        <PopoverMenu.TouchableOption
          title={i18n.t('surveys.analytics.download_csv')}
          onClick={() => onDownload(EXPORT_FORMATS.csv)}
        />
        <PopoverMenu.TouchableOption
          title={i18n.t('surveys.analytics.download_xlsx')}
          onClick={() => onDownload(EXPORT_FORMATS.xlsx)}
        />
      </PopoverMenu.Content>
    </PopoverMenu>
  )
}

export default observer(RightActions)
